// src/pages/imamMahdi/DescribedInQuranHadith.jsx
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const DescribedInQuranHadith = () => {
  return (
    <>
      <Helmet>
        <title>
          Imam Mahdi (ajtf) Described in Qur’an & Hadith | Ziyarat Guide
        </title>
        <meta
          name="description"
          content="Explore how Imam Mahdi (ajtf) is described in the Qur’an and Hadith — verses, prophetic sayings, and Shia interpretations about the awaited savior who will establish justice."
        />
        <meta
          property="og:title"
          content="Imam Mahdi (ajtf) Described in Qur’an & Hadith | Ziyarat Guide"
        />
        <meta
          property="og:description"
          content="Learn the Qur’anic verses and hadith references interpreted by scholars as signs of Imam Mahdi (ajtf) — the one destined to fill the world with justice and peace."
        />
        <meta
          property="og:image"
          content="https://ziyaratguide.com/wp-content/uploads/2025/08/fourth-How-is-Imam-Mahdi-described-in-Qur%CE%93COan-hadith.png"
        />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="imam-page">
        <div className="imam-content">
          <h1 className="page-title">Described In Quran & Hadith</h1>
          <div className="content-section">
            <img
              src="https://ziyaratguide.com/wp-content/uploads/2025/08/fourth-How-is-Imam-Mahdi-described-in-Qur%CE%93COan-hadith.png"
              alt="Imam Mahdi described in Quran & Hadith"
              className="imam-image"
            />

            <p>Verses & sayings interpreted by scholars as references.</p>

            <h2>References in the Qur’an</h2>
            <p>
              Although the Qur’an does not explicitly name Imam Mahdi, Muslim
              scholars explain that many verses allude to the concept of a
              divinely guided leader who will establish global justice.
            </p>
            <p>
              The Imams of Ahlulbayt (as) themselves interpreted several verses
              as referring to Imam Mahdi. For example:
            </p>

            <h3>1. The promise to establish righteous rule</h3>
            <blockquote>
              “Allah has promised those among you who believe and do righteous
              deeds that He will surely grant them succession (authority) upon
              the earth just as He granted it to those before them…” (Qur’an
              24:55)
            </blockquote>
            <p>
              According to Muslim tafsir, this verse points towards the ultimate
              fulfilment through Imam Mahdi, who will lead a community that
              embodies true faith and justice.
            </p>

            <h3>2. Victory of truth over falsehood</h3>
            <blockquote>
              “It is He who sent His Messenger with guidance and the religion of
              truth, to make it prevail over all religion, though the
              polytheists dislike it.” (Qur’an 9:33, also 61:9, 48:28)
            </blockquote>
            <p>
              Shia narrations say the final complete manifestation of this
              triumph will occur with Imam Mahdi’s appearance, when Islam’s
              values of justice, compassion and unity will be fully established
              on earth.
            </p>

            <h3>3. The awaited community of the oppressed</h3>
            <blockquote>
              “And We wished to favour those who were oppressed in the land, and
              to make them leaders and make them inheritors.” (Qur’an 28:5)
            </blockquote>
            <p>
              Imam Ja’far al-Sadiq (as) explained this refers ultimately to the
              followers of the Mahdi who will rise from being downtrodden to
              honoured leaders under his rule.
            </p>

            <h2>Descriptions in Hadith</h2>
            <p>
              The concept of Imam Mahdi is deeply rooted in hadith, found
              abundantly in both Shia and Sunni collections. The narrations
              clearly state:
            </p>
            <ul>
              <li>He is from the family of the Prophet (Ahlulbayt).</li>
              <li>
                His name is Muhammad, his father’s name is Hasan (or in Sunni
                hadith: Abdullah).
              </li>
              <li>He resembles the Prophet in character.</li>
              <li>
                He will fill the earth with justice as it had been filled with
                tyranny.
              </li>
            </ul>

            <h3>Well-known hadith</h3>
            <blockquote>
              “Even if only a day remains of the life of this world, Allah will
              prolong that day until He sends a man from my family, whose name
              is like my name, and whose father’s name is like my father’s name.
              He will fill the earth with justice and equity as it was filled
              with oppression and tyranny.”
            </blockquote>
            <p>
              (Sourced from Sunni collections like Sunan Abu Dawud, Musnad
              Ahmad, as well as Shia texts like Kamal al-Deen and Bihar al-Anwar.)
            </p>

            <h3>In hadith from the Imams of Ahlulbayt (as)</h3>
            <p>Imam Ja’far al-Sadiq (as) said:</p>
            <blockquote>
              “The Qa’im (Mahdi) will cleanse the earth of all oppression and
              injustice and fill it with equity and justice.”
            </blockquote>
            <p>Imam Hasan al-Askari (as) described his son, Imam Mahdi, as:</p>
            <blockquote>
              “My son is the Qa’im, the one who will establish truth and uproot
              falsehood.”
            </blockquote>

            <p>
              The Qur’an lays down the promise of divine justice and leadership
              for the oppressed, and Shia interpretations see its final, full
              realisation in Imam Mahdi (aj). The Prophet (pbuh) and the Imams
              (as) spoke frequently of the Mahdi: a man from the Prophet’s
              lineage who will appear to end tyranny and establish global peace
              and fairness.
            </p>
          </div>
        </div>

        {/* Sidebar Navigation */}
        <aside className="imam-sidebar">
          <h2>Explore More</h2>
          <ul>
            <li>
              <Link to="/imam-mahdi">Who is Imam Mahdi?</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/how-to-talk">How To Talk</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/impact-appearance">
                Impact of His Appearance
              </Link>
            </li>
            <li>
              <Link to="/imam-mahdi/misconceptions">Misconceptions</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/practices-allegiance">
                Practices & Allegiance
              </Link>
            </li>
            <li>
              <Link to="/imam-mahdi/reflections-stories">
                Reflections & Stories
              </Link>
            </li>
            <li>
              <Link to="/imam-mahdi/return">Return</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/signs-reappearance">
                Signs of Reappearance
              </Link>
            </li>
            <li>
              <Link to="/imam-mahdi/supporters">Supporters</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/ultimate-reformer">The Ultimate Reformer</Link>
            </li>
            <li>
              <Link to="/imam-mahdi/why-occultation">Why Occultation</Link>
            </li>
          </ul>
        </aside>
      </div>
    </>
  );
};

export default DescribedInQuranHadith;
